import { useCallback } from 'react'
import { TOKEN_PROGRAM, TOKEN_PROGRAM_2022, solanaClient } from '../clients/SolanaClient'
import { EVMChain, PlugToken } from '../interfaces/common'
import { ensureChainFusionAddresses, walletFromPrincipal } from '../providers/WalletProvider'
import { useCustomTokens, useTokenBalances, useUserEVMChains, useUserTokens } from '../state'
import { addDefaultWalletTokens } from '../utils/addDefaultTokens'
import { getTokenBalances } from '../utils/getTokenBalances'
import getTokenMetadata, { UnknownToken } from '../utils/tokenActions/getMetadata'
import { e8sToDecimals } from '../utils/validationHelper'
import useConfig from './useConfig'

const DUST_THRESHOLD_USD = 0.01
const IMAGE_PROBE_TIMEOUT_MS = 3000

const isValidImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    // Image is a DOM API and does not exist under React Native.
    if (!url || typeof Image === 'undefined') {
      resolve(!!url)
      return
    }

    const img = new Image()

    // Neither onload nor onerror is guaranteed to fire: a host that accepts the
    // connection and then never responds leaves this promise pending forever.
    // Promise.allSettled cannot rescue that, so every logo probe needs its own
    // deadline or one slow host stalls the whole import behind it.
    const timer = setTimeout(() => resolve(false), IMAGE_PROBE_TIMEOUT_MS)
    const settle = (isValid: boolean) => {
      clearTimeout(timer)
      resolve(isValid)
    }

    img.onload = () => settle(true)
    img.onerror = () => settle(false)
    img.src = url
  })
}

const getAllEVMTokens = async (
  ethAddress: string,
  evmChains: EVMChain[]
): Promise<UnknownToken[]> => {
  const body = {
    jsonrpc: '2.0',
    method: 'alchemy_getTokenBalances',
    params: [ethAddress],
    id: 1,
  }

  const chainResults = await Promise.allSettled(
    evmChains.map(async (evmChain) => {
      const response = await fetch(evmChain.rpcEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await response.json()
      const balances: { contractAddress: string; tokenBalance: string }[] =
        data?.result?.tokenBalances || []

      return balances.map((token) => ({
        evmChain,
        chain: evmChain.chainId.toString(),
        address: token.contractAddress,
        balance: BigInt(token.tokenBalance),
      }))
    })
  )

  return chainResults
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value)
    .flat()
}

const fetchTokenInfo = async (tokens: UnknownToken[]) => {
  const result = await Promise.allSettled(
    tokens.map(async ({ balance, address, chain, evmChain }) => {
      if (balance < 1n) return

      const tokenInfo = await getTokenMetadata(address, chain, { evmChain })
      const isValid = tokenInfo && (await isValidImageUrl(tokenInfo?.logo))

      if (isValid) {
        return { balance, token: tokenInfo }
      }
    })
  )

  return result.map((r) => r.status === 'fulfilled' && r.value).filter((r) => !!r)
}

export default () => {
  const { tokens, evmChains, tokenPricesWithChange } = useConfig()
  const { userEVMChains } = useUserEVMChains()
  const { toggleToken, addToken, isTokenAdded } = useUserTokens()
  const { setBalances, setBalance } = useTokenBalances()
  const { addToken: storeCustomToken } = useCustomTokens()

  const addCustomToken = (principalId: string, token: PlugToken, balance: string) => {
    storeCustomToken(principalId, token)
    setBalance(token.id, principalId, balance)
    if (!isTokenAdded(principalId, token.id)) {
      addToken(principalId, token.id)
    }
  }

  const findTokens = useCallback(
    async (principalId: string) => {
      await ensureChainFusionAddresses(principalId)
      const wallet = walletFromPrincipal(principalId)
      const evmChainIds = evmChains.map((chain) => chain.chainId)
      const userChains = evmChains.filter((c) => userEVMChains.includes(c.chainId))

      const lsBalanceFetch = getTokenBalances(wallet, tokens, evmChainIds)
      const unknownTokensFetch = await Promise.allSettled([
        solanaClient.getAllSplTokens(wallet.solAddress, TOKEN_PROGRAM_2022),
        solanaClient.getAllSplTokens(wallet.solAddress, TOKEN_PROGRAM),
        getAllEVMTokens(wallet.ethAddress, userChains),
      ])

      const unknownTokens = await fetchTokenInfo(
        unknownTokensFetch
          .filter((p) => p.status === 'fulfilled')
          .map((p) => p.value)
          .flat()
      )

      unknownTokens.forEach(({ token, balance }) => {
        const addr = token.address?.toLowerCase()

        if (!tokens.some((t) => t.address?.toLowerCase() === addr && t.chain === token.chain)) {
          addCustomToken(principalId, token, balance.toString())
        }
      })

      const balances = await lsBalanceFetch
      setBalances(principalId, balances)

      let isAnyTokenAdded = false

      tokens.forEach((token) => {
        const balance = balances.find((balance) => balance.id === token.id)
        const tokenAdded = isTokenAdded(principalId, token.id)

        const tokenPrice = tokenPricesWithChange[token.id]

        const usdValue =
          tokenPrice && balance
            ? tokenPrice.price * e8sToDecimals(balance?.balance, token.decimals)
            : undefined

        const isDust = typeof usdValue !== 'undefined' && usdValue < DUST_THRESHOLD_USD
        const tokenPresent = balance && parseInt(balance.balance) > 0

        if (tokenPresent && !isDust) {
          isAnyTokenAdded = true
          addToken(principalId, token.id)
        } else if (tokenAdded && (!balance || parseInt(balance?.balance) === 0 || isDust)) {
          toggleToken(principalId, token.id)
        }
      })

      if (!isAnyTokenAdded) {
        addDefaultWalletTokens(principalId)
      }
    },
    [evmChains, addToken, toggleToken, isTokenAdded, tokens, tokenPricesWithChange]
  )

  return { findTokens }
}
